import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { branchActions } from "../slices/branch";
import { BranchMapper } from "../../mappers/BranchMapper";
import { getErrorMessage } from "../../utils/errorUtils";
import { BranchRequestDto } from "../../shared/dtos/branch/BranchRequestDto";
import { BranchResponseDto } from "../../shared/dtos/branch/BranchResponseDto";
import { BranchSummaryDto } from "../../shared/dtos/branch/BranchSummaryDto";
import { UpdateBranchPayload } from "../../shared/interfaces/branch/UpdateBranchPayload";
import { EmailRequestDto } from "../../shared/dtos/common/EmailRequestDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getBranches =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(branchActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<BranchSummaryDto[]>
      >("/branches", { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(branchActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(branchActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const createBranch =
  (values: BranchRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(branchActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<BranchResponseDto>
      >("/branches", values, { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Branch created successfully!");
        const summary = BranchMapper.mapBranchToSummary(data.data);
        dispatch(branchActions.createSuccess(summary));
        dispatch(
          branchActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      dispatch(branchActions.setErrorCreate(getErrorMessage(error)));
    }
  };

export const updateBranch =
  (payload: UpdateBranchPayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(branchActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<BranchResponseDto>
      >(`/branches/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Branch updated!");
        const summary = BranchMapper.mapBranchToSummary(data.data);
        dispatch(branchActions.updateSuccess(summary));
        dispatch(
          branchActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      dispatch(branchActions.setErrorUpdate(getErrorMessage(error)));
    }
  };

export const emailBranchDetails =
  (payload: EmailRequestDto) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(branchActions.setLoadingEmail());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        "/branches/email",
        payload,
        { headers: { requiresAuth: true } },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Email sent!");
        dispatch(branchActions.setEmailSuccess());
        return true;
      }
      return false;
    } catch (error) {
      dispatch(branchActions.setErrorEmailing(getErrorMessage(error)));
      return false;
    }
  };

export const getBranchById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(branchActions.setLoadingFetchOne());

  try {
    const { data } = await axiosInstance.get<ApiResponseDto<BranchResponseDto>>(
      `/users/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(branchActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(branchActions.setErrorFetchOne(getErrorMessage(error)));
  }
};
